/**
 * Previewing an attachment in the browser — Step 73.
 *
 * What can be shown, and why each answer is what it is:
 *
 *   images  rendered directly. Already safe: served with `nosniff`, a
 *           sandboxed CSP, and a type decided by magic bytes, so an "image"
 *           that is really HTML never got stored in the first place.
 *   PDFs    rendered by pdf.js to a canvas, scripting off — see PdfPreview.
 *           The browser never opens the file, so its built-in viewer (the
 *           actual risk) is never involved.
 *   the rest downloads, and SAYS SO. An empty box or a spinner that never
 *           finishes is worse than a sentence explaining there is nothing to
 *           show.
 *
 * Office documents are the interesting gap — see the note by
 * `canPreview` for what it would take and why it is a decision rather than
 * an oversight.
 */
import { Download, FileText } from 'lucide-react';
import { PdfPreview } from './PdfPreview';
import { Button } from '../../components/ui/button';

const OFFICE = [
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
];

/**
 * Whether we can show this in the page at all.
 *
 * Office formats are deliberately absent. The routes NOT taken, so the next
 * person does not re-derive them:
 *
 *  - Microsoft's Office Online embed viewer needs the file to be reachable
 *    by Microsoft, i.e. public. Ours are auth-gated, and shipping customer
 *    documents to a third party to render is not a trade we get to make on
 *    their behalf.
 *  - Server-side conversion (LibreOffice headless) means running a large,
 *    historically vulnerable binary over untrusted input on our own box —
 *    trading a browser-side risk for a far worse server-side one.
 *  - Client-side parsing IS viable and is the route worth taking: mammoth.js
 *    turns .docx into HTML that our existing `cleanArticleHtml` allowlist
 *    can sanitise, and it only ever reads `word/document.xml` — a macro
 *    lives in `vbaProject.bin`, which it does not touch. SheetJS does the
 *    same for .xlsx, though its npm build has lagged its security fixes and
 *    would need pinning carefully. .pptx has no credible pure-JS renderer.
 *
 * That is a dependency decision rather than a technical blocker, so it is
 * Kai's call, not one to make silently.
 */
export function canPreview(contentType: string): boolean {
  return contentType.startsWith('image/') || contentType === 'application/pdf';
}

export function AttachmentViewer({
  id,
  filename,
  contentType,
}: {
  id: string;
  filename: string;
  contentType: string;
}) {
  const url = `/api/media/${id}`;

  if (contentType.startsWith('image/')) {
    return (
      <div className="overflow-auto rounded-md border bg-neutral-100 p-3 dark:bg-neutral-900">
        <img src={url} alt={filename} className="mx-auto max-w-full rounded shadow-sm" />
      </div>
    );
  }

  if (contentType === 'application/pdf') return <PdfPreview url={url} />;

  const isOffice = OFFICE.includes(contentType);
  return (
    <div className="flex flex-col items-center gap-3 rounded-md border border-dashed p-8 text-center">
      <FileText className="h-8 w-8 text-muted-foreground" />
      <div>
        <p className="text-sm font-medium">{filename}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {isOffice
            ? 'Office documents open in their own application — download it to read it.'
            : 'This file type has no in-page preview.'}
        </p>
      </div>
      <Button
        variant="outline"
        size="sm"
        // A download, not a navigation: the file is served with
        // `Content-Disposition: attachment`, so the browser saves it rather
        // than trying to render it in our origin.
        onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
      >
        <Download /> Download
      </Button>
    </div>
  );
}
